import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, Sparkles, Users, BarChart3, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SECONDARY_IMAGE } from "@/lib/constants";

export default function ForBusinesses() {
  return (
    <div className="bg-[#FAFAFA]">
      <section className="relative bg-gradient-to-br from-[#064E3B] to-[#0a6b54] text-white overflow-hidden">
        <img src={SECONDARY_IMAGE} alt="" className="absolute inset-0 w-full h-full object-cover opacity-20" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 mb-5 text-xs font-medium">
              <Sparkles className="w-3 h-3 text-[#B4A081]" /> Free to list — forever
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05] mb-5">
              Grow your IV therapy business with IVTherapyFinder
            </h1>
            <p className="text-lg text-white/85 mb-8 max-w-2xl leading-relaxed">
              Get in front of patients actively searching for IV therapy in your area. Manage reviews, capture leads, and grow your practice — completely free.
            </p>
            <div className="flex flex-wrap gap-3">
              <Button asChild size="lg" className="bg-white text-[#064E3B] hover:bg-stone-100 rounded-full font-semibold">
                <Link to="/add-business" data-testid="biz-cta-list">List your business — free <ArrowRight className="w-4 h-4 ml-2" /></Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="rounded-full text-white border-white/30 hover:bg-white/10 hover:text-white">
                <Link to="/claim-business" data-testid="biz-cta-claim">Claim an existing listing</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { icon: Users, t: "Qualified leads daily", d: "Patients ready to book — not random web traffic. Every lead is geo-targeted and intent-verified." },
            { icon: Star, t: "Reputation management", d: "Respond to reviews, request reviews from past clients, and showcase your best work." },
            { icon: BarChart3, t: "Real-time analytics", d: "Track views, clicks, phone taps, and lead conversion — month over month." },
          ].map((b, i) => (
            <div key={i} className="bg-white rounded-2xl border border-stone-200 p-7">
              <div className="w-12 h-12 rounded-xl bg-[#064E3B]/8 flex items-center justify-center mb-4">
                <b.icon className="w-6 h-6 text-[#064E3B]" />
              </div>
              <h3 className="font-display text-lg font-semibold text-stone-900 mb-2">{b.t}</h3>
              <p className="text-sm text-stone-600 leading-relaxed">{b.d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="py-20 bg-white border-y border-stone-100">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#10B981] mb-3">Always Free</div>
          <h2 className="font-display text-3xl sm:text-4xl font-semibold text-stone-900 mb-4">Everything you need — no credit card required</h2>
          <p className="text-stone-600 mb-10">List your business in minutes and start receiving patient leads today. We may add paid plans later, but the core listing experience stays free.</p>

          <div className="bg-gradient-to-br from-[#064E3B] to-[#0a6b54] rounded-3xl p-8 sm:p-10 text-white text-left">
            <div className="font-display text-lg font-semibold mb-1">Free plan includes</div>
            <div className="font-display text-5xl font-semibold mb-6">$0<span className="text-base font-normal text-white/70"> · always</span></div>
            <ul className="space-y-2.5 text-sm">
              {[
                "Full business profile page (the world-class template)",
                "Unlimited gallery photos (built-in storage)",
                "Reviews & ratings with category breakdown",
                "Appointment requests delivered to your dashboard",
                "Reply publicly to any review",
                "Q&A section for patient questions",
                "AI-generated review summary, auto-updated",
                "Listed in all city and state directory pages",
                "Mobile-friendly profile + click-to-call",
              ].map((f) => (
                <li key={f} className="flex items-start gap-2.5">
                  <CheckCircle2 className="w-4 h-4 text-[#B4A081] mt-0.5 shrink-0" />
                  <span className="text-white/95">{f}</span>
                </li>
              ))}
            </ul>
            <Button asChild size="lg" className="mt-7 bg-white text-[#064E3B] hover:bg-stone-100 rounded-full font-semibold">
              <Link to="/add-business">Get started — list free <ArrowRight className="w-4 h-4 ml-2" /></Link>
            </Button>
          </div>
        </div>
      </section>

      <section className="py-20 max-w-3xl mx-auto px-4 text-center">
        <h2 className="font-display text-3xl font-semibold text-stone-900 mb-3">Already on our directory?</h2>
        <p className="text-stone-600 mb-6">Claim your existing listing to manage photos, respond to reviews, and receive appointment leads.</p>
        <Button asChild size="lg" variant="outline" className="rounded-full">
          <Link to="/claim-business">Claim your listing →</Link>
        </Button>
      </section>
    </div>
  );
}
